<script setup lang="ts">
import { useQueryClient } from '@tanstack/vue-query'
import { useField, useForm } from 'vee-validate'
import * as yup from 'yup'
import { createCurrencyAction } from '@/api/functions/settings/get-currencies-action'
import { useMutationApi } from '@/composables/useApi'
import { useToast } from '@/composables/useToast'
import { getErrorMessage } from '@/utils/errorUtils'

const props = defineProps({
  modelValue: Boolean,
})

const emit = defineEmits(['update:modelValue', 'confirm', 'cancel'])

const toast = useToast()
const queryClient = useQueryClient()

// --- Validation Schema ---
const schema = yup.object({
  title: yup.string().required('نام ارز الزامی است'),
  iso: yup.string().required('کد ارز الزامی است').length(3, 'کد ارز باید 3 حرف باشد'),
  selling_exchange_rate_margin: yup
    .number()
    .required('بازه قیمتی فروش الزامی است')
    .typeError('بازه قیمتی فروش باید عدد باشد'),
  buying_exchange_rate_margin: yup
    .number()
    .required('بازه قیمتی خرید الزامی است')
    .typeError('بازه قیمتی خرید باید عدد باشد'),
})

// --- Form setup ---
const { handleSubmit, errors, resetForm } = useForm({
  validationSchema: schema,
  initialValues: {
    title: '',
    iso: '',
    selling_exchange_rate_margin: null,
    buying_exchange_rate_margin: null,
  },
})

// Fields
const { value: title } = useField('title')
const { value: iso } = useField('iso')
const { value: selling_exchange_rate_margin } = useField('selling_exchange_rate_margin')
const { value: buying_exchange_rate_margin } = useField('buying_exchange_rate_margin')

// --- Mutation ---
const createCurrencyMutation = useMutationApi({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mutationFn: (payload) => createCurrencyAction(payload as any),
  onSuccess: () => {
    toast.success('ارز جدید با موفقیت ایجاد شد', 'موفقیت')
    emit('confirm')
    closeModal()
  },
  onError: (error: Error) => {
    toast.error(getErrorMessage(error), 'خطا')
  },
})

// --- Submit ---
const onSubmit = handleSubmit((values) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return createCurrencyMutation.mutate(values as any)
})

const closeModal = () => {
  emit('update:modelValue', false)
  emit('cancel')
  resetForm()
}
</script>

<template>
  <Modal :model-value="props.modelValue" @update:model-value="$emit('update:modelValue', $event)" width="600px"
    background="#FFF" :closable="true" title="افزودن ارز جدید" closeDirection="left" class="update-request">

    <form @submit.prevent="onSubmit" class="flex flex-col min-h-[430px]">
      <div class="flex-1 overflow-auto">
        <div class="grid px-4 pt-6 pb-9 gap-6">
          <Input v-model="title" name="title" label="نام ارز" type="text" :error="errors.title" />
          <Input v-model="iso" name="iso" label="کد ارز" type="text" :error="errors.iso" />
          <Input v-model="selling_exchange_rate_margin" name="selling_exchange_rate_margin"
            label="بازه قیمتی ثبت پیشنهاد روی درخواست فروش" type="number"
            :error="errors.selling_exchange_rate_margin" />
          <Input v-model="buying_exchange_rate_margin" name="buying_exchange_rate_margin"
            label="بازه قیمتی ثبت پیشنهاد روی درخواست خرید" type="number" :error="errors.buying_exchange_rate_margin" />
        </div>
      </div>

      <div class="sticky bottom-0 bg-white z-50 flex justify-between gap-6 w-full pt-3 pb-9 px-2 border-gray-200">
        <Button type="submit" label="افزودن ارز" :isAdmin="true" class="w-9/12 h-12 rounded-xl"
          :disabled="createCurrencyMutation.isPending.value" :loading="createCurrencyMutation.isPending.value" />
        <Button type="button" @click="closeModal" variant="outline" label="بازگشت" :isAdmin="true"
          class="w-4/12 h-12 rounded-xl border-2" :disabled="createCurrencyMutation.isPending.value" />
      </div>
    </form>
  </Modal>
</template>
